/**
 * Client-side pill counter allowances by plan (menu copy + /tools/pill-counter).
 * Trial (Stripe trialing) or first 30 days on a free account: 2 counts.
 * Pro (active paid): 20 counts.
 * Premium: unlimited.
 */
import { areScanLimitsDisabled } from "@/lib/scanLimitsDisabled";

export const PILL_TRIAL_WINDOW_DAYS = 30;
export const PILL_TRIAL_MAX_USES = 2;
export const PILL_PRO_MAX_USES = 20;

const STORAGE_KEY = "ed_pill_counter_v1";

type PillStore = { count: number };

function readStore(): PillStore {
  if (typeof window === "undefined") return { count: 0 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { count: 0 };
    const p = JSON.parse(raw) as unknown;
    if (p && typeof p === "object" && "count" in p && typeof (p as PillStore).count === "number") {
      return { count: Math.max(0, (p as PillStore).count) };
    }
  } catch {
    /* ignore */
  }
  return { count: 0 };
}

function writeStore(s: PillStore) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export function getPillCount(): number {
  return readStore().count;
}

export function incrementPillCount(): number {
  const next = readStore().count + 1;
  writeStore({ count: next });
  return next;
}

/** Optional audit trail for the tool UI (local only — not synced to Supabase). */
const LOG_KEY = "ed_pill_counter_log_v1";

export type PillCounterLogEntry = { at: number; units: number };

function readLogs(): PillCounterLogEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(LOG_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw) as unknown;
    if (!Array.isArray(p)) return [];
    return p
      .map((row) => {
        if (!row || typeof row !== "object") return null;
        const o = row as Record<string, unknown>;
        const at = typeof o.at === "number" ? o.at : Number(o.at);
        const units = typeof o.units === "number" ? o.units : Number(o.units);
        if (!Number.isFinite(at) || !Number.isFinite(units) || units < 1) return null;
        return { at, units: Math.floor(units) };
      })
      .filter((x): x is PillCounterLogEntry => x != null);
  } catch {
    return [];
  }
}

/** Records units for the success screen / “last count” copy (keeps last 40). */
export function appendPillCounterLog(units: number): void {
  if (typeof window === "undefined" || !Number.isFinite(units) || units < 1) return;
  try {
    const u = Math.min(999999, Math.floor(units));
    const next = [{ at: Date.now(), units: u }, ...readLogs()].slice(0, 40);
    localStorage.setItem(LOG_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function getLastPillCounterLog(): PillCounterLogEntry | null {
  const rows = readLogs();
  return rows.length ? rows[0] : null;
}

export type PillCounterAccess =
  | { ok: true; kind: "premium"; remaining: null }
  | { ok: true; kind: "trial_window" | "trialing" | "pro"; remaining: number; max: number }
  | { ok: false; kind: "locked"; reason: string; remaining: 0; max: number };

export function getPillCounterAccess(args: {
  userId: string | null;
  paidPlan: string;
  subscriptionStatus: string | null;
  userCreatedAtIso: string | null;
}): PillCounterAccess {
  if (areScanLimitsDisabled()) {
    return { ok: true, kind: "premium", remaining: null };
  }

  if (!args.userId) {
    return { ok: false, kind: "locked", reason: "Sign in to use the pill counter.", remaining: 0, max: PILL_TRIAL_MAX_USES };
  }

  const used = getPillCount();

  if (args.paidPlan === "premium") {
    return { ok: true, kind: "premium", remaining: null };
  }

  const created = args.userCreatedAtIso ? new Date(args.userCreatedAtIso).getTime() : 0;
  const inSignupWindow =
    created > 0 && Date.now() < created + PILL_TRIAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;

  if (args.subscriptionStatus === "trialing") {
    if (used < PILL_TRIAL_MAX_USES) {
      return { ok: true, kind: "trialing", remaining: PILL_TRIAL_MAX_USES - used, max: PILL_TRIAL_MAX_USES };
    }
    return {
      ok: false,
      kind: "locked",
      reason: "Trial pill counts used. Upgrade to unlock Pro or Owner limits.",
      remaining: 0,
      max: PILL_TRIAL_MAX_USES,
    };
  }

  if (args.paidPlan === "pro" && args.subscriptionStatus === "active") {
    if (used < PILL_PRO_MAX_USES) {
      return { ok: true, kind: "pro", remaining: PILL_PRO_MAX_USES - used, max: PILL_PRO_MAX_USES };
    }
    return {
      ok: false,
      kind: "locked",
      reason: "Pro pill counter cap reached for this period. Owner edition is unlimited.",
      remaining: 0,
      max: PILL_PRO_MAX_USES,
    };
  }

  if (inSignupWindow) {
    if (used < PILL_TRIAL_MAX_USES) {
      return { ok: true, kind: "trial_window", remaining: PILL_TRIAL_MAX_USES - used, max: PILL_TRIAL_MAX_USES };
    }
    return {
      ok: false,
      kind: "locked",
      reason: "Free exploration counts used. Subscribe for Pro (20) or Owner (unlimited).",
      remaining: 0,
      max: PILL_TRIAL_MAX_USES,
    };
  }

  return {
    ok: false,
    kind: "locked",
    reason: "Pill counter is included on paid plans — choose Pro or Owner.",
    remaining: 0,
    max: PILL_PRO_MAX_USES,
  };
}
